import { createContext, useContext } from 'react';

// Who the Management Client is acting as (documentation/authorization.md, documentation/entra.md).
// The server authenticates and authorizes every request; this only drives which screens and
// networks are offered, and carries the session's CSRF token.

export interface MeNetwork {
  id: string;
  name: string;
  tenant_id: string;
  tenant_name: string;
  tenant_wide: boolean;
  permissions: string[];
}

export interface Me {
  user_id?: string;
  username?: string;
  identity: 'jwt' | 'session' | 'dev-user' | 'dev-platform' | string;
  csrf_token?: string;
  /** For identity "session": "admin" here (the Dispatcher has its own "dispatcher" session). */
  session_audience?: string;
  /**
   * For identity "session": when this session ends (sign-in + the platform's Management Client
   * session lifetime; activity never extends it) and the server's clock when answering. The page
   * signs out at that moment (services/sessionExpiry.ts).
   */
  session_expires_at?: string;
  server_time?: string;
  /** For identity "session": "entra" (Microsoft), "google" or "password" (RadioOS-managed). */
  auth_method?: 'entra' | 'google' | 'password' | string;
  /** How the session was established (server-side), e.g. ["pwd","otp"] or ["entra","mfa"]. */
  amr?: string[];
  dev_mode: boolean;
  platform_admin: boolean;
  platform_operator?: boolean;
  grants: { role: string; tenant_name?: string; department_name?: string }[];
  networks: MeNetwork[];
  // The first OEM Partner the caller holds an OEM role in (compatibility); oem_partners lists all.
  oem_partner?: { id: string; name: string; role: string };
  oem_partners?: { id: string; name: string; role: string }[];
  email?: string;
  display_name?: string;
  /** The caller's MFA policy requires MFA / this session carries MFA assurance (strong session). */
  mfa_required?: boolean;
  mfa_satisfied?: boolean;
  /** The caller administers people somewhere (Settings -> People). */
  manages_users?: boolean;
  dev_users?: { id: string; username: string; legacy_role: string; tenant: string }[];
}

// DEV_MODE only: the server honours X-Dev-User-ID solely when DEV_MODE is on (it is stripped
// otherwise). It lets a developer act as a synthetic tenant/department user without signing in.
const DEV_USER_KEY = 'ptt.devActAsUser';

export function getDevUser(): string {
  try {
    return localStorage.getItem(DEV_USER_KEY) || '';
  } catch {
    return '';
  }
}

export function setDevUser(userId: string) {
  try {
    if (userId) localStorage.setItem(DEV_USER_KEY, userId);
    else localStorage.removeItem(DEV_USER_KEY);
  } catch {
    /* storage unavailable */
  }
}

// CSRF token of the RadioOS session (from GET /api/auth/me), kept in memory only, together with
// who this page signed in as (to tell a renewed session of the same person from a different one).
let csrfToken = '';
let sessionActive = false;
let sessionUser = '';
let sessionIdentity = '';

export function setSession(me: Me | null) {
  csrfToken = me?.csrf_token || '';
  sessionActive = me?.identity === 'session';
  sessionUser = me?.user_id || '';
  sessionIdentity = me?.identity || '';
}

export function getCsrfToken(): string {
  return csrfToken;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/** The exact 403 body of the backend CSRF check (server/pkg/auth/middleware.go, http.Error). */
export const CSRF_REJECTION_TEXT = 'Missing or invalid CSRF token';

export function isCsrfRejection(status: number, body: string): boolean {
  return status === 403 && body.trim() === CSRF_REJECTION_TEXT;
}

// A CSRF rejection is decided before any handler runs, so nothing was changed. The request is
// never replayed automatically: the person sees what happened and retries deliberately.
export const CSRF_RENEWED_MESSAGE =
  'Your sign-in was renewed (for example in another browser tab), so this request was refused and nothing was changed. Please try again.';
export const CSRF_RELOADING_MESSAGE =
  'Your sign-in changed in another browser tab, so this request was refused and nothing was changed. The page is reloading; try again afterwards.';
export const CSRF_STALE_MESSAGE =
  "This page's sign-in is out of date, so the request was refused and nothing was changed. Reload the page (sign in again if asked) and try again.";

/** How long after a CSRF-triggered reload another one is suppressed (reload-loop guard). */
const CSRF_RELOAD_GUARD_MS = 60_000;
const CSRF_RELOAD_KEY = 'ptt.csrfReloadAt';

export interface ApiAuthHost {
  origin: string;
  reload: () => void;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  now?: () => number;
}

let csrfRecovery: (() => Promise<string>) | null = null;

/**
 * After a CSRF rejection: re-reads the current session (GET /api/auth/me) and returns the message
 * to show. The same person's renewed session only replaces the token; a different identity, an
 * ended session or a changed MFA state reloads the page once (guarded against reload loops).
 */
export function recoverFromCsrfRejection(): Promise<string> {
  return csrfRecovery ? csrfRecovery() : Promise.resolve(CSRF_STALE_MESSAGE);
}

/**
 * Wraps a fetch for same-origin /api requests: the HttpOnly session cookie travels automatically;
 * this adds the CSRF header to state-changing requests and the DEV act-as header, returns to the
 * sign-in screen when the session has ended (401), and recovers from a CSRF rejection caused by a
 * session that changed in another tab (the rejected request is answered with an explanation).
 */
export function createApiFetch(original: typeof fetch, host: ApiAuthHost): typeof fetch {
  let reloading = false;
  let recovering: Promise<string> | null = null;

  const reloadOnce = (): boolean => {
    if (reloading) return true;
    const now = (host.now || Date.now)();
    try {
      const last = Number(host.storage?.getItem(CSRF_RELOAD_KEY) || 0);
      if (last && now - last >= 0 && now - last < CSRF_RELOAD_GUARD_MS) return false;
      host.storage?.setItem(CSRF_RELOAD_KEY, String(now));
    } catch {
      /* storage unavailable: the in-page flag still prevents repeated reloads */
    }
    reloading = true;
    host.reload();
    return true;
  };

  const recover = async (): Promise<string> => {
    let me: Me | null = null;
    try {
      const response = await original('/api/auth/me', { credentials: 'same-origin', cache: 'no-store' });
      if (response.status === 401) return reloadOnce() ? CSRF_RELOADING_MESSAGE : CSRF_STALE_MESSAGE;
      if (response.status === 403) return reloadOnce() ? CSRF_RELOADING_MESSAGE : CSRF_STALE_MESSAGE; // e.g. MFA now required
      if (!response.ok) return CSRF_STALE_MESSAGE;
      me = (await response.json()) as Me;
    } catch {
      return CSRF_STALE_MESSAGE;
    }
    const sameSession =
      me?.identity === 'session' && sessionIdentity === 'session' && !!me.user_id && me.user_id === sessionUser && !!me.csrf_token;
    if (sameSession) {
      setSession(me);
      return CSRF_RENEWED_MESSAGE;
    }
    // Another person, another kind of identity (e.g. this page was opened in DEV_MODE) or no
    // usable token: the page's identity is stale, so it is rebuilt from the server.
    return reloadOnce() ? CSRF_RELOADING_MESSAGE : CSRF_STALE_MESSAGE;
  };

  csrfRecovery = () => {
    if (!recovering) recovering = recover().finally(() => { recovering = null; });
    return recovering;
  };

  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const isApi = url.startsWith('/api/') || url.startsWith(host.origin + '/api/');
    if (!isApi) return original(input, init);
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const devUser = getDevUser();
    if (devUser) headers.set('X-Dev-User-ID', devUser);
    if (csrfToken && !SAFE_METHODS.has(method)) headers.set('X-CSRF-Token', csrfToken);
    const response = await original(input, { ...init, headers, credentials: 'same-origin' });
    if (response.status === 401 && sessionActive && !url.includes('/api/auth/') && !reloading) {
      reloading = true;
      host.reload(); // the session expired or was revoked: show the sign-in screen
    }
    if (response.status === 403 && !SAFE_METHODS.has(method)) {
      const body = await response.clone().text().catch(() => '');
      if (isCsrfRejection(response.status, body)) {
        const message = await csrfRecovery!();
        return new Response(message, {
          status: 403,
          statusText: response.statusText,
          headers: { 'Content-Type': 'text/plain; charset=utf-8' },
        });
      }
    }
    return response;
  };
}

/** Installs createApiFetch as the page's fetch. */
export function installApiAuth() {
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    /* storage unavailable */
  }
  window.fetch = createApiFetch(window.fetch.bind(window), {
    origin: window.location.origin,
    reload: () => window.location.reload(),
    storage,
  });
}

export interface AuthConfig {
  entra_enabled: boolean;
  /** Google sign-in: configured for the deployment and enabled in the platform configuration. */
  google_enabled?: boolean;
  password_login_enabled: boolean;
  /** RadioOS-managed e-mail/password sign-in (provisioning and activation links need it). */
  managed_auth_enabled?: boolean;
  dev_mode: boolean;
}

export async function getAuthConfig(): Promise<AuthConfig> {
  const response = await fetch('/api/auth/config');
  if (!response.ok) return { entra_enabled: false, password_login_enabled: false, dev_mode: false };
  return response.json();
}

/** Thrown by getMe when the session is valid but lacks the MFA assurance the person's role needs. */
export class MFAAssuranceRequired extends Error {
  constructor() {
    super('MFA_ASSURANCE_REQUIRED');
  }
}

/** The current identity, or null when there is no valid session/identity (401). */
export async function getMe(): Promise<Me | null> {
  const response = await fetch('/api/auth/me');
  if (response.status === 401) return null;
  if (response.status === 403) {
    const body = await response.json().catch(() => ({}));
    if ((body as { code?: string }).code === 'MFA_ASSURANCE_REQUIRED') throw new MFAAssuranceRequired();
  }
  if (!response.ok) throw new Error(`Identity unavailable (${response.status})`);
  return response.json();
}

/** Starts the backend-controlled Google sign-in, returning to the current page afterwards. */
export function signInWithGoogle() {
  const returnTo = window.location.pathname;
  window.location.href = '/api/auth/google/login?return_to=' + encodeURIComponent(returnTo || '/');
}

/** Starts the backend-controlled Microsoft sign-in, returning to the current page afterwards. */
export function signInWithMicrosoft() {
  const returnTo = window.location.pathname;
  window.location.href = '/api/auth/entra/login?return_to=' + encodeURIComponent(returnTo || '/');
}

/** Ends the RadioOS session and, when Microsoft sign-in is used, signs out of Microsoft too. */
export async function signOut() {
  const response = await fetch('/api/auth/logout', { method: 'POST' });
  const body = response.ok ? await response.json().catch(() => ({})) : {};
  setDevUser('');
  window.location.href = body.end_session_url || '/';
}

/** Sign-in denial messages (the server only says which kind; nothing sensitive). */
export function loginErrorMessage(code: string | null): string | null {
  switch (code) {
    case null:
    case '':
      return null;
    case 'not_authorized':
      return 'Your account is authenticated but is not linked to an active RadioOS user. Ask your administrator for an invitation.';
    case 'expired':
      return 'The sign-in expired or was interrupted. Please try again.';
    case 'microsoft':
      return 'The sign-in was cancelled or did not complete.';
    case 'mfa_required':
      return 'Your role requires multi-factor authentication, and this sign-in did not provide it. Sign in with a RadioOS password and authenticator app, or ask your administrator.';
    case 'disabled':
      return 'This sign-in method is switched off for RadioOS.';
    case 'invitation':
      return 'The invitation could not be accepted with this account: use the account of the invited e-mail address (its address must be verified) that is not already linked to RadioOS.';
    default:
      return 'The sign-in could not be completed. Please try again.';
  }
}

export const IdentityContext = createContext<{ me: Me | null; error: string | null; sessionEndsAt?: number | null }>({ me: null, error: null });

export function useIdentity() {
  return useContext(IdentityContext);
}

/** Permissions the caller holds in a network ([] when unknown). */
export function networkPermissions(me: Me | null, networkId: string): string[] {
  if (!me) return [];
  if (me.platform_admin) return ['*'];
  return me.networks.find((n) => n.id === networkId)?.permissions || [];
}

export function can(me: Me | null, networkId: string, permission: string): boolean {
  const perms = networkPermissions(me, networkId);
  return perms.includes('*') || perms.includes(permission);
}

/**
 * Whether the client acts ONLY as an OEM person (an OEM role, no platform role and no customer
 * network - e.g. an OEM without customers): it then gets only the OEM area. An OEM person whose
 * OEM administers customers gets the normal application for them (OEM-derived authority) plus the
 * OEM area. This selects screens only; the server authorizes every request.
 */
export function isOEMAdmin(me: Me | null): boolean {
  return !!me && !me.platform_admin && !me.platform_operator && !!me.oem_partner?.id && me.networks.length === 0;
}

/**
 * Whether the Dispatcher (/dispatcher/, a separate app on the same origin) is offered: a platform
 * administrator, or a person who may view groups or radios in at least one network. Navigation
 * only - the Dispatcher's every request is authorized by the server.
 */
export function canUseDispatcher(me: Me | null): boolean {
  if (!me) return false;
  if (me.platform_admin) return true;
  return me.networks.some((n) => n.permissions.includes('groups.view') || n.permissions.includes('radios.view'));
}

/** Whether the caller holds any OEM role (the OEM area is offered). */
export function hasOEMRole(me: Me | null): boolean {
  return !!me && !!me.oem_partner?.id;
}
